import "./styles/globals.css";
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { App } from "./App";
import { UnauthenticatedError } from "./lib/apiClient";

// Hard-navigate to /login the first time any query throws UnauthenticatedError.
// Multiple polling queries can 401 in the same tick (the wallboard runs ~5
// queries on a 5-second poll); the lock prevents redirect spam.
let isRedirectingForAuth = false;
function redirectToLoginOnAuthError(error: unknown): void {
  if (isRedirectingForAuth) return;
  if (!(error instanceof UnauthenticatedError)) return;
  isRedirectingForAuth = true;
  // Only forward an explicit reason code (e.g. user_not_authorized). The
  // generic "unauthenticated" code is the no-session case — sending users to
  // a vanilla /login keeps the message clean.
  const code = error.code && error.code !== "unauthenticated" ? error.code : null;
  const target = code ? `/login?error=${encodeURIComponent(code)}` : "/login";
  window.location.replace(target);
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: redirectToLoginOnAuthError,
  }),
  defaultOptions: {
    queries: {
      // Don't retry 401s — that races with the session-destroy on the server
      // and can lose the original error code (e.g. user_not_authorized) by the
      // time onError finally fires.
      retry: (failureCount, error) =>
        !(error instanceof UnauthenticatedError) && failureCount < 1,
      refetchOnWindowFocus: false,
    },
  },
});

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Missing #root element");

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
